import React, { useEffect, useState, useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { useLocation } from "react-router-dom";
import {
  Table,
  Tag,
  Button,
  Modal,
  Tooltip,
  Spin,
  message,
} from "antd";
import {
  ArrowRightOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  UserOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  BarChartOutlined,
} from "@ant-design/icons";
import { CSVLink } from "react-csv";
import PageHeader from "../../components/generic/PageHeader";
import {
  getResultSummaryByKey,
  getTestResultDetail,
  getTestResultLog,
  getTestResultLogFail,
  getTestResultSummary,
} from "../../service/result.service";
import {
  arrayFilterByColumn,
  arrayFilterByTest,
} from "../../helper/array_helper";
import { getMasterSuiteLibrary } from "../../service/mastersuite.service";
import MasterSuite from "../mastersuite/MasterSuite";
import { labelForSubCategory } from "../../constants/reconciliationLabels";
import "../../styles/results.css";
import {
  StatsStrip,
  MiniBar,
  StatusBadge,
} from "../../components/results/ResultStats";

// ─── Shared style tokens ──────────────────────────────────────
const S = {
  passBg: "#EAF3DE",
  passText: "#3B6D11",
  failBg: "#FCEBEB",
  failText: "#A32D2D",
  blueBg: "#E6F1FB",
  blueText: "#185FA5",
  greenBg: "#EAF3DE",
  greenText: "#3B6D11",
  ambBg: "#FAEEDA",
  ambText: "#854F0B",
  border: "0.5px solid var(--color-border-tertiary, #e8e8e8)",
  radius: 8,
  radiusLg: 10,
};

function CatBadge({ type }) {
  const isCol = type === "Column Level";
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 500,
        background: isCol ? S.greenBg : S.blueBg,
        color: isCol ? S.greenText : S.blueText,
      }}
    >
      {type}
    </span>
  );
}

// ─── Parse source names from suiteName "a<>b" ────────────────
function parseSources(suiteName = "", subCategory = "") {
  const parts = suiteName.split("<>");
  const s1 = parts[0]?.trim() || "";
  const s2 = parts[1]?.split("&&")[0]?.trim() || parts[1]?.trim() || "";
  const sub = subCategory.toLowerCase();
  if (sub === "file to file")
    return { source1: s1 || "File 1", source2: s2 || "File 2" };
  if (sub === "file to database")
    return { source1: s1 || "File", source2: s2 || "Database" };
  if (sub === "database to database")
    return { source1: s1 || "DB 1", source2: s2 || "DB 2" };
  return { source1: s1 || "Source 1", source2: s2 || "Source 2" };
}

// ─── Interactive stacked bar chart ───────────────────────────
function ResultBarChart({ testSuiteResult, onToggleExpand }) {
  if (!testSuiteResult || testSuiteResult.length === 0) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 80, color: "#bbb", fontSize: 12 }}>
        No data yet
      </div>
    );
  }

  const names = testSuiteResult.map((r) => {
    const n = r.testName || "";
    return n.length > 44 ? n.slice(0, 42) + "…" : n;
  });

  const series = [
    { name: "Pass", data: testSuiteResult.map((r) => r.passCount || 0) },
    { name: "Fail", data: testSuiteResult.map((r) => r.failCount || 0) },
    { name: "N/A",  data: testSuiteResult.map((r) => r.naCount  || 0) },
  ];

  const options = {
    chart: {
      type: "bar",
      stacked: true,
      toolbar: { show: false },
      fontFamily: "inherit",
      events: {
        dataPointSelection: (_e, _ctx, { dataPointIndex }) => {
          const row = testSuiteResult[dataPointIndex];
          if (row && onToggleExpand) onToggleExpand(row.key);
        },
      },
    },
    plotOptions: {
      bar: { horizontal: true, barHeight: "55%", borderRadius: 4, borderRadiusApplication: "end" },
    },
    colors: ["#3B6D11", "#A32D2D", "#d1d5db"],
    xaxis: { categories: names, labels: { style: { fontSize: "11px", colors: "#888" } } },
    yaxis: { labels: { style: { fontSize: "11px", colors: "#555" }, maxWidth: 220 } },
    legend: { position: "bottom", fontSize: "11px", markers: { size: 8, shape: "square" } },
    tooltip: {
      custom: ({ dataPointIndex }) => {
        const row = testSuiteResult[dataPointIndex];
        if (!row) return "";
        const total = row.testCount || (row.passCount || 0) + (row.failCount || 0) + (row.naCount || 0);
        const passRate = total > 0 ? Math.round(((row.passCount || 0) / total) * 100) : 0;
        const naLine = row.naCount > 0 ? `<div style="color:#ccc;margin-top:2px">— N/A: <b>${row.naCount}</b></div>` : "";
        return `<div style="padding:10px 14px;background:#0B1F3A;color:#fff;border-radius:8px;font-size:12px;min-width:180px;box-shadow:0 4px 16px rgba(0,0,0,0.3)">
          <div style="font-weight:600;margin-bottom:6px;color:#93C5FD">${row.testName}</div>
          <div style="color:#86EFAC">✓ Passed: <b>${row.passCount || 0}</b></div>
          <div style="color:#FCA5A5;margin-top:2px">✗ Failed: <b>${row.failCount || 0}</b></div>
          ${naLine}
          <div style="border-top:0.5px solid rgba(255,255,255,0.2);margin-top:6px;padding-top:5px;color:#aaa;font-size:11px">${passRate}% pass rate · click to expand</div>
        </div>`;
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (v) => (v > 0 ? v : ""),
      style: { fontSize: "10px", colors: ["#fff"] },
    },
    grid: { borderColor: "#f0f0f0", padding: { left: 0, right: 10 } },
    states: { active: { allowMultipleDataPointsSelection: true, filter: { type: "darken", value: 0.8 } } },
  };

  const height = Math.max(180, testSuiteResult.length * 46 + 65);
  return <ReactApexChart type="bar" options={options} series={series} height={height} />;
}

// ─── Meta info row ────────────────────────────────────────────
function MetaRow({ label, children }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        padding: "6px 0",
        borderBottom: S.border,
        gap: 8,
        fontSize: 13,
      }}
    >
      <span
        style={{
          color: "var(--color-text-secondary, #888)",
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontWeight: 500,
          textAlign: "right",
          wordBreak: "break-word",
          maxWidth: "65%",
        }}
      >
        {children}
      </span>
    </div>
  );
}

// ─── Parameters renderer (reuses JobDetails logic) ────────────
function ParametersDisplay({
  value = "",
  suiteName = "",
  subCategory = "",
  onMasterSuiteClick,
}) {
  const prefix = subCategory.toLowerCase();
  if (prefix === "database to database") {
    const [a, b] = value.split("<>");
    return (
      <span>
        <b>First DB: </b>
        {a?.trim()}
        <br />
        <b>Second DB: </b>
        {b?.trim()}
      </span>
    );
  }
  if (prefix === "file to file") {
    const [a, b] = value.split("<>");
    return (
      <span>
        <b>First File: </b>
        {a?.trim()}
        <br />
        <b>Second File: </b>
        {b?.trim()}
      </span>
    );
  }
  if (prefix === "file to database") {
    const [filePart, rest] = value.split("<>");
    const dbPart = rest?.split("&&")[0];
    return (
      <span>
        <b>File: </b>
        {filePart?.trim()}
        <br />
        <b>Database: </b>
        {dbPart?.trim()}
      </span>
    );
  }
  if (prefix === "file to specification") {
    return (
      <span>
        <b>File: </b>
        {value}
        <br />
        <b>Test Suite: </b>
        {suiteName}
      </span>
    );
  }
  if (prefix === "n/a") {
    return (
      <a onClick={onMasterSuiteClick} style={{ color: "#185FA5" }}>
        <b>Master Suite: </b>
        {value}
      </a>
    );
  }
  return <span>{value}</span>;
}

// ─── Failure detail modal ─────────────────────────────────────
function DetailLogModal({
  open,
  onClose,
  logData,
  source1,
  source2,
  testName,
  csvFilename,
  rowKeyColumns,
}) {
  const isRowTest = ["exists", "duplicate", "row key", "number of rows"].some(
    (k) => testName?.toLowerCase().includes(k),
  );

  // Header name / case tests describe a column-naming problem — show it plainly
  // (Expected spec column · Column in file · Issue) instead of the generic layout.
  const isHeaderNameTest = [
    "column header name",
    "header row case sensitive",
  ].some((k) => testName?.toLowerCase().includes(k));

  // The new field-level validations describe a value-level problem — show it as
  // (Column · Value · Expected · Issue) with a clear tag, like the header tests.
  const isFieldIssueTest = [
    "data type validation",
    "date/time format validation",
    "text format validation",
    "mandatory column",
  ].some((k) => testName?.toLowerCase().includes(k));

  const issueMeta = (reason) => {
    const r = (reason || "").toLowerCase();
    if (r.includes("case mismatch")) return { color: "orange", label: "Case mismatch" };
    if (r.includes("spelling")) return { color: "gold", label: "Spelling?" };
    if (r.includes("missing mandatory")) return { color: "red", label: "Missing column" };
    if (r.includes("numeric")) return { color: "volcano", label: "Not numeric" };
    if (r.includes("boolean")) return { color: "volcano", label: "Not boolean" };
    if (r.includes("date")) return { color: "geekblue", label: "Bad date" };
    if (r.includes("pattern")) return { color: "purple", label: "Bad pattern" };
    if (r.includes("exceeds")) return { color: "red", label: "Too large" };
    if (r.includes("fewer records")) return { color: "red", label: "Too few rows" };
    if (r.includes("file name")) return { color: "red", label: "Name format" };
    if (r.includes("missing")) return { color: "red", label: "Missing" };
    return { color: "default", label: "Mismatch" };
  };

  const fieldIssueCols = [
    {
      title: "Column",
      dataIndex: "columnName",
      key: "columnName",
      ellipsis: true,
      render: (v) => (
        <span style={{ fontSize: 12, fontWeight: 600, color: "#0B1F3A" }}>
          {v || "—"}
        </span>
      ),
    },
    {
      title: "Value",
      dataIndex: "actualValue",
      key: "actualValue",
      render: (v) => (
        <code
          style={{
            fontSize: 12,
            background: "#fdeeee",
            padding: "1px 6px",
            borderRadius: 3,
            color: S.failText,
          }}
        >
          {v === "" || v == null ? "(empty)" : v}
        </code>
      ),
    },
    {
      title: "Expected",
      dataIndex: "expectedValue",
      key: "expectedValue",
      render: (v) => (
        <span style={{ fontSize: 12, color: "#555" }}>{v || "—"}</span>
      ),
    },
    {
      title: "Issue",
      dataIndex: "rawData",
      key: "rawData",
      render: (v) => {
        const m = issueMeta(v);
        return (
          <span>
            <Tag color={m.color} style={{ marginRight: 6 }}>
              {m.label}
            </Tag>
            <span style={{ fontSize: 12, color: "#555" }}>{v}</span>
          </span>
        );
      },
    },
  ];

  const headerNameCols = [
    {
      title: "Expected (spec column)",
      dataIndex: "expectedValue",
      key: "expectedValue",
      render: (v) => (
        <code
          style={{
            fontSize: 12,
            background: "#f5f5f5",
            padding: "1px 6px",
            borderRadius: 3,
            color: S.blueText,
          }}
        >
          {v || "—"}
        </code>
      ),
    },
    {
      title: "Column in file",
      dataIndex: "actualValue",
      key: "actualValue",
      render: (v) =>
        !v || v === "(not in file)" ? (
          <span style={{ fontSize: 12, color: "#999", fontStyle: "italic" }}>
            {v || "(not in file)"}
          </span>
        ) : (
          <code
            style={{
              fontSize: 12,
              background: "#fdeeee",
              padding: "1px 6px",
              borderRadius: 3,
              color: S.failText,
            }}
          >
            {v}
          </code>
        ),
    },
    {
      title: "Issue",
      dataIndex: "rawData",
      key: "rawData",
      render: (v) => {
        const m = issueMeta(v);
        return (
          <span>
            <Tag color={m.color} style={{ marginRight: 6 }}>
              {m.label}
            </Tag>
            <span style={{ fontSize: 12, color: "#555" }}>{v}</span>
          </span>
        );
      },
    },
  ];

  const cols = isHeaderNameTest
    ? headerNameCols
    : isFieldIssueTest
    ? fieldIssueCols
    : [
    {
      title: "Identifier",
      dataIndex: "rawData",
      key: "rawData",
      width: 120,
      render: (v) => (
        <span style={{ fontSize: 12, fontFamily: "monospace", fontWeight: 600, color: "#0B1F3A" }}>
          {v || "—"}
        </span>
      ),
    },
    {
      title: "Column Name",
      dataIndex: "columnName",
      key: "columnName",
      ellipsis: true,
      render: (v) => (
        <Tooltip title={v}>
          <code
            style={{
              fontSize: 11,
              background: "#f5f5f5",
              padding: "1px 5px",
              borderRadius: 3,
            }}
          >
            {v || "—"}
          </code>
        </Tooltip>
      ),
    },
    isRowTest
      ? {
          title: "Detail",
          dataIndex: "actualValue",
          key: "actualValue",
          render: (v) => <span style={{ fontSize: 13 }}>{v || "—"}</span>,
        }
      : {
          title: (
            <span>
              <span style={{ color: S.failText, fontWeight: 500 }}>
                {source1}
              </span>
              <span style={{ color: "#888", fontSize: 11, marginLeft: 4 }}>
                (actual)
              </span>
            </span>
          ),
          dataIndex: "actualValue",
          key: "actualValue",
          render: (v) => (
            <span
              style={{
                fontSize: 12,
                color: S.failText,
                fontWeight: 500,
                wordBreak: "break-word",
              }}
            >
              {v || "—"}
            </span>
          ),
        },
    isRowTest
      ? null
      : {
          title: (
            <span>
              <span style={{ color: S.blueText, fontWeight: 500 }}>
                {source2}
              </span>
              <span style={{ color: "#888", fontSize: 11, marginLeft: 4 }}>
                (expected)
              </span>
            </span>
          ),
          dataIndex: "expectedValue",
          key: "expectedValue",
          render: (v) => (
            <span style={{ fontSize: 12, wordBreak: "break-word" }}>
              {v || "—"}
            </span>
          ),
        },
  ].filter(Boolean);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={700}
      destroyOnClose
      title={
        <div>
          <div style={{ fontWeight: 500, fontSize: 14 }}>Failure detail</div>
          {testName && (
            <div
              style={{
                fontSize: 12,
                color: "#888",
                fontWeight: 400,
                marginTop: 2,
              }}
            >
              {testName}
            </div>
          )}
        </div>
      }
      footer={
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            alignItems: "center",
          }}
        >
          <CSVLink
            data={logData || []}
            filename={csvFilename || "fail-log.csv"}
          >
            <Button icon={<DownloadOutlined />}>Export CSV</Button>
          </CSVLink>
          <Button type="primary" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      {/* Row key column(s) info */}
      {rowKeyColumns && rowKeyColumns.length > 0 && (
        <div style={{
          display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
          background: "#EFF6FF", border: "1px solid #BFDBFE",
          borderRadius: 6, padding: "7px 12px", marginBottom: 10, fontSize: 12,
        }}>
          <span style={{ color: "#1D4ED8", fontWeight: 500 }}>
            Matched on:
          </span>
          {rowKeyColumns.map((col) => (
            <span key={col} style={{
              fontFamily: "monospace", fontWeight: 600, fontSize: 11,
              background: "#DBEAFE", color: "#1E40AF",
              padding: "1px 8px", borderRadius: 10,
            }}>
              {col}
            </span>
          ))}
          {rowKeyColumns.length > 1 && (
            <span style={{ color: "#6B7280", fontSize: 11 }}>
              (composite key — values concatenated as Identifier)
            </span>
          )}
        </div>
      )}

      {!isRowTest && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "#fafafa",
            border: S.border,
            borderRadius: 6,
            padding: "8px 12px",
            marginBottom: 12,
            fontSize: 12,
            flexWrap: "wrap",
          }}
        >
          <span style={{ color: S.failText, fontWeight: 500 }}>
            ● {source1}
          </span>
          <ArrowRightOutlined style={{ color: "#bbb" }} />
          <span style={{ color: S.blueText, fontWeight: 500 }}>
            ● {source2}
          </span>
          <span style={{ color: "#888", marginLeft: 4 }}>
            Red = {source1} · Blue = {source2}
          </span>
        </div>
      )}
      <Table
        columns={cols}
        dataSource={logData || []}
        size="small"
        rowKey={(r) => r.key || r.row}
        pagination={{ defaultPageSize: 20, showSizeChanger: true }}
        scroll={{ x: true }}
      />
    </Modal>
  );
}

// ─── Test suite table (compare + spec) ───────────────────────
function TestSuiteTable({
  testSuiteResult,
  testResultDetail,
  testFailLog,
  testAllLog,
  loading,
  logLoading,
  subCategory,
  suiteName,
  isSpec,
  expandedKeys,
  onExpandedKeysChange,
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [modalTitle, setModalTitle] = useState("");
  const [modalCsv, setModalCsv] = useState("fail-log.csv");
  const { source1, source2 } = parseSources(suiteName, subCategory);

  // Derive row key column names from log entries for row-key tests.
  // The backend joins multiple key column names with "-" into a single columnName
  // (e.g. "ID-Ticker"), so we split on "-" to get individual column names.
  // Hyphens are safe to split on because sanitiseColName() converts hyphens in
  // real column names to underscores before they reach the temp table.
  const rowKeyColumns = useMemo(() => {
    const ROW_KEY_KEYWORDS = ["duplicate", "exists in"];
    const keyEntries = (testAllLog || []).filter((entry) =>
      ROW_KEY_KEYWORDS.some((k) => entry.testName?.toLowerCase().includes(k)),
    );
    const names = keyEntries.flatMap((e) =>
      (e.columnName || "").split("-").filter(Boolean),
    );
    return [...new Set(names)];
  }, [testAllLog]);

  const openModal = (data, title, csv) => {
    setModalData(data || []);
    setModalTitle(title || "");
    setModalCsv(csv || "fail-log.csv");
    setModalOpen(true);
  };

  // ─── Column Level reconciliation ─────────────────────────────
  // The backend column comparison counts a "row key not found" (a record missing
  // from the second source) as a Fail for EVERY column. That missing record is
  // already reported by the Row Level existence test, and we hide those rows from
  // the column log — so here we also subtract them from each column's Rows/Fail
  // counts and recompute the status, keeping the numbers consistent with the
  // (filtered) failure list. e.g. 99 rows with 1 missing → 98 compared, not "Fail 1".
  const isMissingRecordRow = (e) =>
    /row key not found/i.test(e?.expectedValue || "") ||
    /row key not found/i.test(e?.actualValue || "");

  const colDetailAdjusted = useMemo(() => {
    const detail = testResultDetail || [];
    const missingByTest = {};
    (testFailLog || []).forEach((e) => {
      if (e.testName?.startsWith("Source 1") && isMissingRecordRow(e)) {
        missingByTest[e.testName] = (missingByTest[e.testName] || 0) + 1;
      }
    });
    return detail.map((d) => {
      const miss = d.testName?.startsWith("Source 1")
        ? missingByTest[d.testName] || 0
        : 0;
      if (!miss) return d;
      const failCount = Math.max(0, (Number(d.failCount) || 0) - miss);
      const testCount = Math.max(0, (Number(d.testCount) || 0) - miss);
      return {
        ...d,
        failCount,
        testCount,
        testStatus: failCount > 0 ? "Fail" : "Pass",
      };
    });
  }, [testResultDetail, testFailLog]);

  // Group source1 rows for compare (not spec)
  const processedData = useMemo(() => {
    if (isSpec) {
      // Cluster the spec tests by level so the result reads top-down:
      // File-level checks → Column-level (structure) → Field-level (values).
      const order = { "File Level": 0, "Column Level": 1, "Field Level": 2 };
      return [...(testSuiteResult || [])].sort(
        (a, b) => (order[a.testType] ?? 3) - (order[b.testType] ?? 3),
      );
    }
    const src1 = colDetailAdjusted.filter((i) =>
      i.testName?.startsWith("Source 1"),
    );
    const grouped = {
      key: "Source1Key",
      testName: "Compare values for rows existing in both data sources",
      testType: "Column Level",
      testCount: src1.length,
      passCount: src1.filter((i) => i.testStatus === "Pass").length,
      failCount: src1.filter((i) => i.testStatus === "Fail").length,
      naCount: 0,
      testStatus: src1.some((i) => i.testStatus === "Fail") ? "Fail" : "Pass",
    };
    const rest = (testSuiteResult || []).filter(
      (i) => !i.testName?.startsWith("Source 1"),
    );
    return [grouped, ...rest];
  }, [colDetailAdjusted, testSuiteResult, isSpec]);

  // Top-level columns
  const topCols = [
    {
      title: "Category",
      dataIndex: "testType",
      key: "testType",
      width: 130,
      render: (v) => <CatBadge type={v} />,
    },
    {
      title: "Test",
      dataIndex: "testName",
      key: "testName",
      render: (v) => <span style={{ fontSize: 13 }}>{v}</span>,
    },
    {
      title: "Scenarios",
      dataIndex: "testCount",
      key: "testCount",
      width: 90,
      align: "right",
    },
    {
      title: "Pass",
      dataIndex: "passCount",
      key: "passCount",
      width: 60,
      align: "right",
      render: (v) => (
        <span
          style={{
            color: v > 0 ? S.passText : "#bbb",
            fontWeight: v > 0 ? 500 : 400,
          }}
        >
          {v}
        </span>
      ),
    },
    {
      title: "Fail",
      dataIndex: "failCount",
      key: "failCount",
      width: 60,
      align: "right",
      render: (v) => (
        <span
          style={{
            color: v > 0 ? S.failText : "#bbb",
            fontWeight: v > 0 ? 600 : 400,
          }}
        >
          {v}
        </span>
      ),
    },
    {
      title: "Pass rate",
      key: "passRate",
      width: 120,
      render: (_, r) => <MiniBar pass={r.passCount} total={r.testCount} />,
    },
    {
      title: "Status",
      dataIndex: "testStatus",
      key: "testStatus",
      width: 100,
      align: "center",
      filters: [
        { text: "Passed", value: "Pass" },
        { text: "Failed", value: "Fail" },
      ],
      onFilter: (v, r) => r.testStatus === v,
      render: (v) => <StatusBadge status={v} />,
    },
  ];

  // Expanded row
  const expandedRowRender = (record) => {
    const isColLevel =
      record.testName ===
      "Compare values for rows existing in both data sources";
    let data = [],
      logData = [];

    if (isColLevel) {
      // Use the reconciled per-column counts (missing records subtracted).
      data = colDetailAdjusted.filter((i) =>
        i.testName?.startsWith("Source 1"),
      );
      // Hide "Row key not found…" entries from the Column Level log — those
      // missing records are already reported by the Row Level existence test,
      // so showing them here just duplicates that detail. (Row Level uses the
      // wording "Record missing", so this filter never touches row-level rows.)
      logData = (testFailLog || []).filter(
        (i) => i.testName?.startsWith("Source 1") && !isMissingRecordRow(i),
      );
    } else if (isSpec) {
      data =
        arrayFilterByTest(testResultDetail || [], record.testName) ||
        arrayFilterByTest(testAllLog || [], record.testName);
      logData = arrayFilterByTest(testFailLog || [], record.testName);
    } else {
      data = arrayFilterByTest(testResultDetail || [], record.testName);
      logData = arrayFilterByTest(testFailLog || [], record.testName);
    }

    // Column-level detail columns (compare)
    const colCols = [
      {
        title: (
          <span>
            Column{" "}
            <span
              style={{
                color: "#888",
                fontWeight: 400,
                fontSize: 11,
                marginLeft: 4,
              }}
            >
              ({source1} <ArrowRightOutlined /> {source2})
            </span>
          </span>
        ),
        dataIndex: "testName",
        key: "testName",
        render: (text) => {
          const p = (text || "").split("\n");
          const c1 = p[0]?.replace("Source 1 Column: ", "") || text;
          const c2 = p[1]?.replace("Source 2 Column: ", "") || "";
          return c2 ? (
            <span style={{ fontSize: 13 }}>
              <code
                style={{
                  background: S.blueBg,
                  color: S.blueText,
                  padding: "1px 5px",
                  borderRadius: 3,
                }}
              >
                {c1}
              </code>
              <ArrowRightOutlined style={{ margin: "0 6px", color: "#bbb" }} />
              <code
                style={{
                  background: S.blueBg,
                  color: S.blueText,
                  padding: "1px 5px",
                  borderRadius: 3,
                }}
              >
                {c2}
              </code>
            </span>
          ) : (
            <code
              style={{
                fontSize: 12,
                background: "#f5f5f5",
                padding: "1px 5px",
                borderRadius: 3,
              }}
            >
              {c1}
            </code>
          );
        },
      },
      {
        title: "Rows",
        dataIndex: "testCount",
        key: "testCount",
        width: 70,
        align: "right",
      },
      {
        title: "Pass",
        dataIndex: "passCount",
        key: "passCount",
        width: 60,
        align: "right",
        render: (v) => (
          <span style={{ color: v > 0 ? S.passText : "#bbb" }}>{v}</span>
        ),
      },
      {
        title: "Fail",
        dataIndex: "failCount",
        key: "failCount",
        width: 70,
        align: "right",
        render: (v, row) =>
          v < 1 ? (
            <span style={{ color: "#bbb" }}>0</span>
          ) : (
            <a
              style={{
                color: S.failText,
                fontWeight: 600,
                textDecoration: "underline",
              }}
              onClick={() => {
                const colName = (row.testName || "").split("\n")[0]
                  .replace("Source 1 Column: ", "").trim();
                const colFiltered = arrayFilterByColumn(logData, colName);
                openModal(
                  colFiltered,
                  record.testName,
                  `fail-${colName}.csv`,
                );
              }}
            >
              {v}
            </a>
          ),
      },
      {
        title: "Pass rate",
        key: "passRate",
        width: 110,
        render: (_, r) => <MiniBar pass={r.passCount} total={r.testCount} />,
      },
      {
        title: "Status",
        dataIndex: "testStatus",
        key: "testStatus",
        width: 90,
        align: "center",
        render: (v) => <StatusBadge status={v} />,
      },
    ];

    // Row-level / spec detail columns
    const rowCols = [
      {
        title: "Column / Key",
        dataIndex: "columnName",
        key: "columnName",
        render: (v) => (
          <code
            style={{
              fontSize: 11,
              background: "#f5f5f5",
              padding: "1px 5px",
              borderRadius: 3,
            }}
          >
            {v || "—"}
          </code>
        ),
      },
      {
        title: "Rows",
        dataIndex: "testCount",
        key: "testCount",
        width: 70,
        align: "right",
      },
      {
        title: "Pass",
        dataIndex: "passCount",
        key: "passCount",
        width: 60,
        align: "right",
        render: (v) => (
          <span style={{ color: v > 0 ? S.passText : "#bbb" }}>{v}</span>
        ),
      },
      {
        title: "Fail",
        dataIndex: "failCount",
        key: "failCount",
        width: 70,
        align: "right",
        render: (v, row) =>
          v < 1 ? (
            <span style={{ color: "#bbb" }}>0</span>
          ) : (
            <a
              style={{
                color: S.failText,
                fontWeight: 600,
                textDecoration: "underline",
              }}
              onClick={() => {
                const f = arrayFilterByColumn(logData, row.columnName);
                openModal(
                  f.length > 0 ? f : logData,
                  record.testName,
                  `fail-${record.testName}.csv`,
                );
              }}
            >
              {v}
            </a>
          ),
      },
      {
        title: "Pass rate",
        key: "passRate",
        width: 110,
        render: (_, r) => <MiniBar pass={r.passCount} total={r.testCount} />,
      },
      {
        title: "Status",
        dataIndex: "testStatus",
        key: "testStatus",
        width: 90,
        align: "center",
        render: (v) => <StatusBadge status={v} />,
      },
    ];

    return (
      <div
        style={{
          margin: "4px 0 4px 32px",
          background: "#fafafa",
          border: S.border,
          borderRadius: 6,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "7px 12px",
            background: "#f5f5f5",
            borderBottom: S.border,
            fontSize: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span style={{ color: "#555" }}>
            {isColLevel
              ? `Column-level comparison: ${source1} → ${source2}`
              : record.testName}
          </span>
          {record.failCount > 0 && (
            <a
              style={{ color: S.failText, fontSize: 12 }}
              onClick={() =>
                openModal(
                  logData,
                  record.testName,
                  `fail-${record.testName}.csv`,
                )
              }
            >
              View all {record.failCount} failures →
            </a>
          )}
        </div>
        <Table
          columns={isColLevel ? colCols : rowCols}
          dataSource={data}
          size="small"
          rowKey="key"
          pagination={{ defaultPageSize: 10, showSizeChanger: true }}
          loading={logLoading}
          style={{ fontSize: 13 }}
        />
      </div>
    );
  };

  const totalPassed = processedData.filter(
    (r) => r.testStatus === "Pass",
  ).length;
  const totalFailed = processedData.filter(
    (r) => r.testStatus === "Fail",
  ).length;

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
          padding: "0 2px",
        }}
      >
        <div>
          <span style={{ fontSize: 14, fontWeight: 500 }}>
            Test suite results
          </span>
          <span style={{ fontSize: 12, color: "#888", marginLeft: 10 }}>
            {processedData.length} tests · {totalPassed} passed · {totalFailed}{" "}
            failed
          </span>
        </div>
        {!isSpec && suiteName && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: "#f5f5f5",
              borderRadius: 6,
              padding: "4px 10px",
              fontSize: 12,
              color: "#555",
            }}
          >
            <span style={{ fontWeight: 500 }}>{source1}</span>
            <ArrowRightOutlined style={{ color: "#bbb" }} />
            <span style={{ fontWeight: 500 }}>{source2}</span>
          </div>
        )}
      </div>
      <div
        style={{
          background: "#fff",
          border: S.border,
          borderRadius: S.radiusLg,
          overflow: "hidden",
        }}
      >
        <Table
          columns={topCols}
          dataSource={processedData}
          expandable={{
            expandedRowRender,
            rowExpandable: () => true,
            expandedRowKeys: expandedKeys || [],
            onExpand: (expanded, record) => {
              if (!onExpandedKeysChange) return;
              onExpandedKeysChange((prev) =>
                expanded
                  ? [...(prev || []), record.key]
                  : (prev || []).filter((k) => k !== record.key),
              );
            },
          }}
          rowKey="key"
          loading={loading}
          size="middle"
          rowClassName={(r) => (r.testStatus === "Fail" ? "arc-fail-row" : "")}
          pagination={false}
          style={{ fontSize: 13 }}
        />
      </div>
      <DetailLogModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        logData={modalData}
        source1={source1}
        source2={source2}
        testName={modalTitle}
        csvFilename={modalCsv}
        rowKeyColumns={rowKeyColumns}
      />
      <style>{`.arc-fail-row td{background:#fff9f9!important}.arc-fail-row:hover td{background:#fff2f2!important}`}</style>
    </div>
  );
}

// ─── Main ResultDetail page ───────────────────────────────────
function ResultDetail() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const resultSummaryKey = location.state?.key || queryParams.get("key");

  const [jobData, setJobData] = useState(null);
  const [testSuiteResult, setTestSuiteResult] = useState([]);
  const [testResultDetail, setTestResultDetail] = useState([]);
  const [testFailLog, setTestFailLog] = useState([]);
  const [testAllLog, setTestAllLog] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [logLoading, setLogLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(true);
  const [msOpen, setMsOpen] = useState(false);
  const [msData, setMsData] = useState(null);
  const [msLoading, setMsLoading] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState([]);

  useEffect(() => {
    if (!resultSummaryKey) return;
    getResultSummaryByKey(resultSummaryKey)
      .then((res) => {
        setJobData(res.data);
        setPageLoading(false);
        getTestResultSummary(resultSummaryKey).then((sr) => {
          setTestSuiteResult(sr.data || []);
          getTestResultDetail(resultSummaryKey).then((dr) => {
            setTableLoading(false);
            setTestResultDetail(dr.data || []);
            getTestResultLog(resultSummaryKey).then((lr) => {
              setLogLoading(false);
              setTestAllLog(lr.data || []);
            });
            getTestResultLogFail(resultSummaryKey, "Fail").then((fr) => {
              setTestFailLog(fr.data || []);
            });
          });
        });
      })
      .catch(() => setPageLoading(false));
  }, [resultSummaryKey]);

  const openMasterSuite = () => {
    setMsOpen(true);
    setMsLoading(true);
    setMsData(null);
    getMasterSuiteLibrary()
      .then((res) => {
        setMsData(res.data?.filter((d) => d.name === jobData?.inputName) || []);
      })
      .finally(() => setMsLoading(false));
  };

  const toggleExpand = (key) => {
    setExpandedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  // All hooks before any early returns
  const sub = jobData?.subCategory || "";
  const suiteName = jobData?.suiteName || jobData?.inputName || "";
  const isSpec = sub === "File to Specification" || sub === "File To Spec";
  const overallPass = jobData?.testResult === "Pass";

  const processedTestData = useMemo(() => {
    if (!jobData) return [];
    if (isSpec) return testSuiteResult;
    const src1 = testResultDetail.filter((i) =>
      i.testName?.startsWith("Source 1"),
    );
    const grouped = {
      key: "Source1Key",
      testName: "Column level compare",
      testType: "Column Level",
      testCount: src1.length,
      passCount: src1.filter((i) => i.testStatus === "Pass").length,
      failCount: src1.filter((i) => i.testStatus === "Fail").length,
      naCount: 0,
      testStatus: src1.some((i) => i.testStatus === "Fail") ? "Fail" : "Pass",
    };
    const rest = testSuiteResult.filter(
      (i) => !i.testName?.startsWith("Source 1"),
    );
    return [grouped, ...rest];
  }, [testResultDetail, testSuiteResult, isSpec, jobData]);

  // Early returns after all hooks
  if (pageLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }
  if (!jobData) return null;

  // Stats
  const totalTests = testSuiteResult.length;
  const passed = testSuiteResult.filter((r) => r.testStatus === "Pass").length;
  const failed = testSuiteResult.filter((r) => r.testStatus === "Fail").length;
  const failureRows = testFailLog.length;
  const passRate = totalTests > 0 ? Math.round((passed / totalTests) * 100) : 0;

  return (
    <div style={{ padding: "16px 24px" }}>
      {/* ── Header ─────────────────────────────────────────── */}
      <PageHeader
        backTo={-1}
        icon={<BarChartOutlined />}
        title={sub || "Result Detail"}
        subtitle={
          <div
            style={{
              display: "flex",
              gap: 16,
              flexWrap: "wrap",
              fontSize: 12,
              color: "var(--color-text-secondary, #888)",
            }}
          >
            <span>
              <UserOutlined style={{ marginRight: 4 }} />
              {jobData.user || "—"}
            </span>
            <span>
              <CalendarOutlined style={{ marginRight: 4 }} />
              Started {jobData.runStart || "—"}
            </span>
            {jobData.runEnd && (
              <span>
                <ClockCircleOutlined style={{ marginRight: 4 }} />
                Ended {jobData.runEnd}
              </span>
            )}
            <span style={{ fontFamily: "monospace", fontSize: 11 }}>
              {resultSummaryKey}
            </span>
          </div>
        }
        actions={
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              background: overallPass ? S.passBg : S.failBg,
              color: overallPass ? S.passText : S.failText,
              padding: "3px 12px",
              borderRadius: 20,
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            {overallPass ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
            {overallPass ? "Passed" : "Failed"}
          </span>
        }
      />

      {/* ── Stats strip ────────────────────────────────────── */}
      <StatsStrip
        items={[
          { label: "Total tests", value: totalTests, color: "#93C5FD" },
          { label: "Passed", value: passed, color: "#86EFAC" },
          { label: "Failed", value: failed, color: failed > 0 ? "#FCA5A5" : "rgba(255,255,255,0.35)" },
          { label: "Failure rows", value: failureRows, color: failureRows > 0 ? "#FCD34D" : "rgba(255,255,255,0.35)" },
        ]}
        segments={[
          { value: passed, color: "#86EFAC" },
          { value: failed, color: "#FCA5A5" },
        ]}
      />

      {/* ── Test inputs + Pie chart ─────────────────────────── */}
      <div
        style={{
          display: "flex",
          gap: 12,
          marginBottom: 20,
          alignItems: "flex-start",
        }}
      >
        {/* Test inputs — compact, left */}
        <div
          style={{
            background: "var(--color-background-secondary, #f5f5f5)",
            borderRadius: S.radiusLg,
            padding: "14px 16px",
            minWidth: 320,
            maxWidth: 400,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 500,
              color: "var(--color-text-secondary, #888)",
              textTransform: "uppercase",
              letterSpacing: 0.5,
              marginBottom: 10,
            }}
          >
            Test inputs
          </div>
          <MetaRow label="Type">
            {sub ? labelForSubCategory(sub) : "—"}
          </MetaRow>
          <MetaRow label="Parameters">
            <ParametersDisplay
              value={jobData.inputName || ""}
              suiteName={jobData.suiteName || ""}
              subCategory={sub}
              onMasterSuiteClick={openMasterSuite}
            />
          </MetaRow>
          <MetaRow label="Environment">
            {jobData.suiteEnvironment || "—"}
          </MetaRow>
          <MetaRow label="Run by">{jobData.user || "—"}</MetaRow>
          <MetaRow label="Started">{jobData.runStart || "—"}</MetaRow>
          <MetaRow label="Ended">{jobData.runEnd || "—"}</MetaRow>
        </div>

        {/* Pie chart — right */}
        <div
          style={{
            background: "var(--color-background-secondary, #f5f5f5)",
            borderRadius: S.radiusLg,
            padding: "14px 16px",
            flex: 1,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 500,
              color: "var(--color-text-secondary, #888)",
              textTransform: "uppercase",
              letterSpacing: 0.5,
              marginBottom: 4,
            }}
          >
            Result breakdown
          </div>
          <div
            style={{
              fontSize: 12,
              color: "var(--color-text-secondary, #888)",
              marginBottom: 8,
            }}
          >
            Column and row level test results
          </div>
          <ResultBarChart
            testSuiteResult={processedTestData}
            onToggleExpand={toggleExpand}
          />
        </div>
      </div>

      {/* ── Test suite results ──────────────────────────────── */}
      <TestSuiteTable
        testSuiteResult={testSuiteResult}
        testResultDetail={testResultDetail}
        testFailLog={testFailLog}
        testAllLog={testAllLog}
        loading={tableLoading}
        logLoading={logLoading}
        subCategory={sub}
        suiteName={suiteName}
        isSpec={isSpec}
        expandedKeys={expandedKeys}
        onExpandedKeysChange={setExpandedKeys}
      />

      {/* ── Master Suite modal ──────────────────────────────── */}
      <Modal
        width="90%"
        open={msOpen}
        onCancel={() => setMsOpen(false)}
        footer={[
          <Button key="ok" type="primary" onClick={() => setMsOpen(false)}>
            OK
          </Button>,
        ]}
      >
        <MasterSuite embed filterData={msData ?? []} loading={msLoading} />
      </Modal>
    </div>
  );
}

export default ResultDetail;
